import { useState, useCallback } from 'react';

export interface ValidationRule<T> {
  validate: (value: T) => string | null;
}

export interface UseFormValidationOptions<T> {
  initialValue: T;
  rules: ValidationRule<T>[];
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

export interface UseFormValidationReturn<T> {
  value: T;
  error: string;
  hasAttemptedSubmit: boolean;
  hasBlurred: boolean;
  setValue: (value: T) => void;
  setError: (error: string) => void;
  setHasAttemptedSubmit: (value: boolean) => void;
  handleChange: (newValue: T) => void;
  handleBlur: () => void;
  validate: () => string | null;
  reset: () => void;
}

/**
 * Hook réutilisable pour la validation de formulaires
 * Gère la validation en temps réel, au blur et à la soumission
 */
export function useFormValidation<T>({
  initialValue,
  rules,
  validateOnBlur = true,
  validateOnChange = true,
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> {
  const [value, setValue] = useState<T>(initialValue);
  const [error, setError] = useState<string>('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const validate = useCallback((): string | null => {
    for (const rule of rules) {
      const errorMessage = rule.validate(value);
      if (errorMessage) {
        return errorMessage;
      }
    }
    return null;
  }, [value, rules]);

  const handleChange = useCallback(
    (newValue: T) => {
      setValue(newValue);

      if (hasAttemptedSubmit || hasBlurred) {
        if (validateOnChange) {
          const validationError = validate();
          setError(validationError || '');
        }
      } else {
        setError('');
      }
    },
    [hasAttemptedSubmit, hasBlurred, validateOnChange, validate]
  );

  const handleBlur = useCallback(() => {
    setHasBlurred(true);
    if (validateOnBlur) {
      const validationError = validate();
      setError(validationError || '');
    }
  }, [validateOnBlur, validate]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setError('');
    setHasAttemptedSubmit(false);
    setHasBlurred(false);
  }, [initialValue]);

  return {
    value,
    error,
    hasAttemptedSubmit,
    hasBlurred,
    setValue,
    setError,
    setHasAttemptedSubmit,
    handleChange,
    handleBlur,
    validate,
    reset,
  };
}
